// Apolo IDE — useApoloRulesAutoLoad (Sprint v11 E4).
//
// When a session opens, attempt to read `.apolorules` (or `.apolo/rules.yml`)
// from the worktree root via the Electron file IPC bridge. If present, PUT
// the content to the Worker so subsequent chat turns get the rules injected
// into their system prompt. Silent no-op when:
//   - Electron bridge is absent (web preview / tests)
//   - No `directory` on the session
//   - Neither candidate file exists (ENOENT)
//   - The Worker PUT fails (best-effort; the user can always edit via RulesTab)

import { useEffect } from 'react';
import { hasElectronBridge } from '../lib/electronBridge.js';
import { putSessionRulesYaml } from '../lib/sessionRulesSync.js';

const CANDIDATES = ['.apolorules', '.apolo/rules.yml'];

async function tryReadFile(root, candidate) {
  if (!hasElectronBridge()) return null;
  try {
    const res = await window.apolo.file.read({ root, filePath: candidate });
    return typeof res?.content === 'string' ? res.content : null;
  } catch (e) {
    // ENOENT = expected miss; any other error → log + swallow.
    const msg = e?.message || '';
    if (/ENOENT|not found/i.test(msg)) return null;
    if (typeof window !== 'undefined' && window.console) {
      window.console.warn('[apolo-rules] read failed', candidate, msg);
    }
    return null;
  }
}

async function putRules(sessionId, yaml) {
  await putSessionRulesYaml(sessionId, yaml);
}

/**
 * @param {string|undefined} sessionId
 * @param {string|undefined} directory
 */
export function useApoloRulesAutoLoad(sessionId, directory) {
  useEffect(() => {
    if (!sessionId || !directory) return undefined;
    if (!hasElectronBridge()) return undefined;
    let cancelled = false;
    (async () => {
      for (const candidate of CANDIDATES) {
        const content = await tryReadFile(directory, candidate);
        if (cancelled) return;
        if (content && content.trim()) {
          await putRules(sessionId, content);
          return;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId, directory]);
}
